import { Navigate, Link } from "react-router-dom";
import { Helmet } from "react-helmet-async";
import {
    Phone, Globe2, ArrowRight, CheckCircle2, Star, Award, Users, Clock,
    Video, GraduationCap, ShieldCheck, MessageCircle, BadgeDollarSign,
} from "lucide-react";
import PageHeader from "../components/PageHeader";
import RegistrationForm from "../components/RegistrationForm";
import SEO from "../components/SEO";
import Breadcrumbs from "../components/Breadcrumbs";
import { COUNTRIES_DATA } from "../data/countriesData";
import { COUNTRY_COURSE_MAP, COURSE_LABELS } from "../data/countryCourseMap";
import { SOCIAL } from "../data/siteData";
import { QuickAnswerCard, DefinitionBox, ConversationalFAQ, buildConversationalFAQSchema } from "../components/AEOContent";
import { buildCountryAEO } from "../data/aeoContent";
import { getInternationalHreflangs } from "../lib/hreflang";
import RotatingTestimonials from "../components/RotatingTestimonials";
import { getTestimonialsForSlug } from "../data/testimonialsPool2G";

const ENQUIRY_PHONE = "+91 70005 30316";
const ENQUIRY_PHONE_RAW = "+917000530316";

const COUNTRY_CODE = {
    usa: "US",
    uk: "GB",
    australia: "AU",
    canada: "CA",
    uae: "AE",
    singapore: "SG",
    "saudi-arabia": "SA",
    germany: "DE",
    "new-zealand": "NZ",
    qatar: "QA",
    kuwait: "KW",
    oman: "OM",
    bahrain: "BH",
    france: "FR",
    netherlands: "NL",
    ireland: "IE",
    switzerland: "CH",
    norway: "NO",
    sweden: "SE",
    finland: "FI",
    denmark: "DK",
    belgium: "BE",
    italy: "IT",
    spain: "ES",
    japan: "JP",
    "south-korea": "KR",
    malaysia: "MY",
};

// hreflang region codes for proper international targeting — exact mapping
// Google expects for en-XX alternate tags. Pages use shared English content
// targeted to each country audience.
const HREFLANG_REGION = {
    usa: "en-US",
    uk: "en-GB",
    australia: "en-AU",
    canada: "en-CA",
    uae: "en-AE",
    singapore: "en-SG",
    "saudi-arabia": "en-SA",
    germany: "en-DE",
    "new-zealand": "en-NZ",
    qatar: "en-QA",
    kuwait: "en-KW",
    oman: "en-OM",
    bahrain: "en-BH",
    france: "en-FR",
    netherlands: "en-NL",
    ireland: "en-IE",
    switzerland: "en-CH",
    norway: "en-NO",
    sweden: "en-SE",
    finland: "en-FI",
    denmark: "en-DK",
    belgium: "en-BE",
    italy: "en-IT",
    spain: "en-ES",
    japan: "en-JP",
    "south-korea": "en-KR",
    malaysia: "en-MY",
};

export default function OnlineCountry({ countrySlug }) {
    const data = COUNTRIES_DATA[countrySlug];
    if (!data) return <Navigate to="/" replace />;

    const eduSchema = {
        "@context": "https://schema.org",
        "@type": "EducationalOrganization",
        "name": `Anand Tutorials — Online Tuition in ${data.fullName}`,
        "image": "https://anandtutorials.com/logo-anand-tutorials.png",
        "@id": `https://anandtutorials.com/${data.urlSlug}`,
        "url": `https://anandtutorials.com/${data.urlSlug}`,
        "telephone": ENQUIRY_PHONE,
        "email": "anandhometutorials@gmail.com",
        "areaServed": {
            "@type": "Country",
            "name": data.fullName,
        },
        "address": {
            "@type": "PostalAddress",
            "addressCountry": COUNTRY_CODE[data.slug] || data.slug.toUpperCase(),
        },
        "priceRange": data.currency === "USD" ? "$$" : data.currency === "GBP" ? "££" : "$$",
        "aggregateRating": {
            "@type": "AggregateRating",
            "ratingValue": data.rating.value,
            "reviewCount": data.rating.count.replace(/,/g, ""),
        },
        "sameAs": [SOCIAL.facebook, SOCIAL.instagram].filter(Boolean),
    };

    const faqSchema = {
        "@context": "https://schema.org",
        "@type": "FAQPage",
        "mainEntity": data.faqs.map(([q, a]) => ({
            "@type": "Question",
            "name": q,
            "acceptedAnswer": { "@type": "Answer", "text": a },
        })),
    };

    // Course schema — published rich-snippet eligibility for country pages.
    const courseSchema = {
        "@context": "https://schema.org",
        "@type": "Course",
        "name": `1:1 Live Online Tuition for ${data.fullName} Students`,
        "description": data.seoDescription || data.intro,
        "url": `https://anandtutorials.com/${data.urlSlug}`,
        "provider": {
            "@type": "EducationalOrganization",
            "@id": "https://anandtutorials.com/#organization",
            "name": "Anand Tutorials",
            "sameAs": "https://anandtutorials.com",
        },
        "audience": {
            "@type": "EducationalAudience",
            "educationalRole": "student",
            "geographicArea": {
                "@type": "Country",
                "name": data.fullName,
            },
        },
        "hasCourseInstance": [{
            "@type": "CourseInstance",
            "courseMode": "online",
            "inLanguage": "en",
            "name": `1:1 Live Online Tuition · ${data.fullName}`,
            "location": {
                "@type": "VirtualLocation",
                "url": `https://anandtutorials.com/${data.urlSlug}`,
            },
            "offers": [{
                "@type": "Offer",
                "availability": "https://schema.org/InStock",
                "price": "0",
                "priceCurrency": data.currency || "INR",
                "category": "Free demo class",
                "url": "https://anandtutorials.com/contact",
            }],
        }],
        "aggregateRating": {
            "@type": "AggregateRating",
            "ratingValue": data.rating.value,
            "reviewCount": String(data.rating.count).replace(/,/g, ""),
            "bestRating": "5",
            "worstRating": "1",
        },
    };

    // V64 — Country-scoped Service schema (AI answer engines cite this when
    // users ask "1:1 online tutor near me" from within the target country).
    const canonicalCountryUrl = `https://anandtutorials.com/${data.urlSlug}`;
    const serviceSchema = {
        "@context": "https://schema.org",
        "@type": "Service",
        "@id": `${canonicalCountryUrl}#service`,
        "serviceType": `1:1 Live Online Tuition for ${data.fullName} students`,
        "name": `Anand Tutorials — Online Tuition in ${data.fullName}`,
        "provider": { "@id": "https://anandtutorials.com/#organization" },
        "areaServed": {
            "@type": "Country",
            "name": data.fullName,
        },
        "audience": {
            "@type": "EducationalAudience",
            "educationalRole": "student",
            "geographicArea": { "@type": "Country", "name": data.fullName },
        },
        "category": "Educational Services",
        "url": canonicalCountryUrl,
        "hasOfferCatalog": {
            "@type": "OfferCatalog",
            "name": `1:1 Online Tuition in ${data.fullName}`,
            "itemListElement": [
                "CBSE 1:1 Online Tuition",
                "ICSE / ISC 1:1 Online Tuition",
                "IB MYP & DP 1:1 Online Tuition",
                "IGCSE Cambridge 1:1 Online Tuition",
                "AS / A-Level 1:1 Online Tuition",
                "AP 1:1 Online Tuition",
                "SAT · ACT · AMC 1:1 Prep",
                "NEET 1:1 Mentorship",
                "JEE Main + Advanced 1:1 Mentorship",
                "1:1 Homework Help",
                "1:1 Exam Preparation",
                "1:1 Spoken English",
            ].map((n) => ({ "@type": "Offer", "itemOffered": { "@type": "Service", "name": n } })),
        },
    };

    // V64 — WebPage schema with Speakable (voice-search / AI Overviews TTS hint).
    const webPageSchema = {
        "@context": "https://schema.org",
        "@type": "WebPage",
        "@id": `${canonicalCountryUrl}#webpage`,
        "url": canonicalCountryUrl,
        "name": data.seoTitle,
        "description": data.seoDescription,
        "inLanguage": "en",
        "isPartOf": { "@id": "https://anandtutorials.com/#organization" },
        "about": { "@id": `${canonicalCountryUrl}#service` },
        "primaryImageOfPage": {
            "@type": "ImageObject",
            "url": "https://anandtutorials.com/logo-anand-tutorials.png",
        },
        "breadcrumb": { "@id": `${canonicalCountryUrl}#breadcrumb` },
        "speakable": {
            "@type": "SpeakableSpecification",
            "cssSelector": ["h1", "[data-speakable]", "details summary", "details > div"],
        },
    };

    // V64 — BreadcrumbList: Home › Online Classes › <Country>
    const breadcrumbSchema = {
        "@context": "https://schema.org",
        "@type": "BreadcrumbList",
        "@id": `${canonicalCountryUrl}#breadcrumb`,
        "itemListElement": [
            { "@type": "ListItem", "position": 1, "name": "Home", "item": "https://anandtutorials.com/" },
            { "@type": "ListItem", "position": 2, "name": "Online Classes", "item": "https://anandtutorials.com/online-classes" },
            { "@type": "ListItem", "position": 3, "name": data.fullName, "item": canonicalCountryUrl },
        ],
    };

    // V65 — AEO / GEO content (Quick Answer + Definition + Conversational FAQ)
    const countryAEO = buildCountryAEO(data);

    return (
        <div data-testid={`online-country-${data.slug}`}>
            <SEO
                title={data.seoTitle}
                description={data.seoDescription}
                keywords={data.keywords}
                path={`/${data.urlSlug}`}
                hreflangs={getInternationalHreflangs()}
                schemas={[eduSchema, faqSchema, courseSchema, serviceSchema, webPageSchema, breadcrumbSchema, buildConversationalFAQSchema(countryAEO.faqs)]}
            />

            {/* V66 — hreflang cluster now emitted centrally by <SEO hreflangs={...}> above.
                Legacy inline Helmet block removed to eliminate duplicate alternate tags. */}

            <Breadcrumbs items={[
                { name: "Online Classes", to: "/online-classes" },
                { name: data.fullName },
            ]} />

            <PageHeader
                eyebrow={`International · ${data.fullName} ${data.flag}`}
                title={data.h1}
                subtitle={data.intro}
            >
                <div className="flex flex-wrap gap-3">
                    <a
                        href={`tel:${ENQUIRY_PHONE_RAW}`}
                        data-testid={`country-${data.slug}-call`}
                        className="inline-flex items-center gap-2 bg-amber-500 text-[#0F172A] font-semibold px-5 py-3 rounded-lg hover:bg-amber-400 transition-colors"
                    >
                        <Phone className="w-4 h-4" /> Call India Team
                    </a>
                    <Link
                        to="/register"
                        data-testid={`country-${data.slug}-demo`}
                        className="inline-flex items-center gap-2 bg-white/10 border border-white/20 backdrop-blur text-white font-semibold px-5 py-3 rounded-lg hover:bg-white/15"
                    >
                        Book Free Demo <ArrowRight className="w-4 h-4" />
                    </Link>
                    <a
                        href={`https://wa.me/${SOCIAL.whatsapp}?text=${encodeURIComponent(
                            `Hi Anand Tutorials, I am from ${data.fullName} and would like to enquire about 1:1 online tuition.`
                        )}`}
                        target="_blank"
                        rel="noreferrer"
                        data-testid={`country-${data.slug}-whatsapp`}
                        className="inline-flex items-center gap-2 bg-[#25D366] text-white font-semibold px-5 py-3 rounded-lg hover:bg-[#1ebe5a]"
                    >
                        <MessageCircle className="w-4 h-4" /> WhatsApp
                    </a>
                </div>
            </PageHeader>

            {/* V65 — AEO Quick-Answer card (TL;DR for AI Overviews / ChatGPT / Perplexity) */}
            <QuickAnswerCard
                question={countryAEO.quickAnswer.question}
                answer={countryAEO.quickAnswer.answer}
                bullets={countryAEO.quickAnswer.bullets}
                footer={countryAEO.quickAnswer.footer}
                testId={`country-${data.slug}-quick-answer`}
            />

            {/* Quick facts strip */}
            <section className="bg-white border-b border-slate-200">
                <div className="max-w-7xl mx-auto px-6 lg:px-8 py-8 grid grid-cols-2 md:grid-cols-4 gap-6 text-center">
                    {[
                        { i: BadgeDollarSign, n: data.currency, l: "Pay in your currency" },
                        { i: Clock, n: data.timezones.split("·")[0].trim(), l: `Time zones: ${data.timezones}` },
                        { i: Star, n: `${data.rating.value} / 5`, l: `${data.rating.count} ${data.name} parents` },
                        { i: Video, n: "Live 1:1", l: "Zoom · Google Meet · Recorded" },
                    ].map(({ i: I, n, l }) => (
                        <div key={l}>
                            <I className="w-6 h-6 text-[#1E3A8A] mx-auto mb-2" />
                            <div className="font-heading text-2xl sm:text-3xl font-bold text-[#0F172A]">{n}</div>
                            <div className="text-xs text-slate-500 mt-0.5">{l}</div>
                        </div>
                    ))}
                </div>
            </section>

            {/* SEO sections */}
            <section className="py-16 bg-white">
                <div className="max-w-4xl mx-auto px-6 lg:px-8 space-y-10">
                    {data.sections.map((s, i) => (
                        <article key={i}>
                            <h2 className="font-heading text-2xl sm:text-3xl font-bold mb-4 text-[#0F172A]">{s.h2}</h2>
                            <p className="text-slate-700 leading-relaxed text-base sm:text-lg">{s.p}</p>
                        </article>
                    ))}
                </div>
            </section>

            {/* Cities served in country */}
            <section className="py-16 bg-[#F8FAFC]">
                <div className="max-w-7xl mx-auto px-6 lg:px-8">
                    <div className="text-xs font-semibold uppercase tracking-[0.3em] text-[#3B82F6] mb-3">
                        Cities We Serve in {data.fullName}
                    </div>
                    <h2 className="font-heading text-3xl sm:text-4xl font-bold mb-3">
                        Online tutors for students across {data.fullName}
                    </h2>
                    <p className="text-slate-600 mb-8 max-w-3xl">
                        Anand Tutorials delivers live 1:1 online tuition to {data.name} students in every major city — from
                        big metros to small towns. If your city isn't listed, call us — we already cover it.
                    </p>
                    <div className="grid sm:grid-cols-2 md:grid-cols-3 lg:grid-cols-4 gap-3">
                        {data.cities.map((c) => (
                            <div
                                key={c}
                                className="bg-white border border-slate-200 rounded-lg px-4 py-3 text-sm text-slate-700 flex items-center gap-2"
                            >
                                <CheckCircle2 className="w-4 h-4 text-amber-500 shrink-0" />
                                <span>Online tuition in {c}</span>
                            </div>
                        ))}
                    </div>
                </div>
            </section>

            {/* Popular course tracks for this country — Batch 2K reverse cross-links */}
            {COUNTRY_COURSE_MAP[data.slug] && COUNTRY_COURSE_MAP[data.slug].length > 0 && (
                <section className="py-16 bg-white border-t border-slate-100" data-testid={`country-${data.slug}-related-courses`}>
                    <div className="max-w-7xl mx-auto px-6 lg:px-8">
                        <div className="text-xs font-semibold uppercase tracking-[0.3em] text-emerald-600 mb-3">Popular in {data.name}</div>
                        <h2 className="font-heading text-3xl sm:text-4xl font-bold text-[#0F172A] mb-3">
                            The 1:1 course tracks {data.name} parents ask for most.
                        </h2>
                        <p className="text-slate-600 mb-8 max-w-3xl">
                            Each pillar below is our dedicated 1:1 mentor track for {data.name}-based students — board-code-matched, syllabus-fluent and rate-transparent in {data.currency}. Tap any card for the exam-pattern deep-dive, sample outcomes and free-demo booking.
                        </p>
                        <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-4">
                            {COUNTRY_COURSE_MAP[data.slug].map(([courseSlug, tagline], i) => (
                                <Link
                                    key={courseSlug}
                                    to={`/courses/${courseSlug}`}
                                    data-testid={`country-${data.slug}-course-${i}`}
                                    className="group flex items-start gap-3 p-5 rounded-xl bg-gradient-to-br from-emerald-50 to-white border border-emerald-100 hover:border-emerald-500 hover:shadow-sm transition-all"
                                >
                                    <div className="w-9 h-9 rounded-lg bg-emerald-600/10 text-emerald-700 flex items-center justify-center shrink-0 group-hover:bg-emerald-600 group-hover:text-white transition-colors">
                                        <GraduationCap className="w-5 h-5" />
                                    </div>
                                    <div className="min-w-0">
                                        <div className="font-heading font-semibold text-[#0F172A] text-sm mb-1 group-hover:text-emerald-700">
                                            {COURSE_LABELS[courseSlug]} in {data.name}
                                        </div>
                                        <div className="text-xs text-slate-600 leading-relaxed">{tagline}</div>
                                    </div>
                                </Link>
                            ))}
                        </div>
                    </div>
                </section>
            )}

            {/* Why us */}
            <section className="py-16 bg-white">
                <div className="max-w-7xl mx-auto px-6 lg:px-8">
                    <div className="text-xs font-semibold uppercase tracking-[0.3em] text-[#3B82F6] mb-3">
                        Why {data.name} Parents Choose Us
                    </div>
                    <h2 className="font-heading text-3xl sm:text-4xl font-bold mb-8">
                        Premium teaching, {data.currency}-friendly pricing, time-zone aligned.
                    </h2>
                    <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-5">
                        {[
                            [GraduationCap, "Cambridge / Oxford / IIT educated tutors", `Less than 12% of applicants make it into our network. Each ${data.name}-facing tutor is hand-picked, demo-tested and background-verified.`],
                            [Video, "Live 1:1 — never recorded lectures", "One student, one tutor, one personalised plan. Two-way HD video on Zoom or Google Meet, recorded for revision."],
                            [Globe2, `${data.timezones} scheduling`, `Tutors specifically work ${data.name}-aligned shifts so your child learns at their best after-school time, not in the middle of the night.`],
                            [BadgeDollarSign, `Pay in ${data.currency}`, `Invoice in ${data.currency} via Wise, Revolut, PayPal, Stripe or international wire. No hidden conversion penalties. Monthly billing, cancel anytime.`],
                            [ShieldCheck, "Free tutor replacement", "If you're not satisfied after the first 2 sessions, we rematch the tutor at no cost — no questions asked."],
                            [Award, "Weekly Sunday parent reports", "What was covered, homework assigned, next week's plan — clear, written, every week."],
                        ].map(([I, t, d]) => (
                            <div key={t} className="p-6 bg-[#F8FAFC] rounded-2xl border border-slate-200">
                                <div className="w-12 h-12 rounded-xl bg-blue-50 text-[#1E3A8A] flex items-center justify-center mb-3">
                                    <I className="w-6 h-6" />
                                </div>
                                <div className="font-heading font-semibold text-[#0F172A] mb-1">{t}</div>
                                <div className="text-sm text-slate-600 leading-relaxed">{d}</div>
                            </div>
                        ))}
                    </div>
                </div>
            </section>

            {/* Services / quick CTAs */}
            <section className="py-16 bg-[#F8FAFC]">
                <div className="max-w-7xl mx-auto px-6 lg:px-8">
                    <div className="text-xs font-semibold uppercase tracking-[0.3em] text-[#3B82F6] mb-3">
                        Programs in {data.fullName}
                    </div>
                    <h2 className="font-heading text-3xl sm:text-4xl font-bold mb-8">
                        Curricula taught for {data.name} students
                    </h2>
                    <div className="grid md:grid-cols-2 gap-6">
                        <div className="bg-white border border-slate-200 rounded-2xl p-6 hover:border-blue-200 transition-colors">
                            <div className="w-12 h-12 rounded-xl bg-blue-50 text-[#1E3A8A] flex items-center justify-center mb-4">
                                <Video className="w-6 h-6" />
                            </div>
                            <h3 className="font-heading text-xl font-semibold mb-2">1:1 Live Online Tuition</h3>
                            <p className="text-slate-600 leading-relaxed text-sm mb-3">
                                One student, one tutor, one screen. Live two-way HD video with whiteboard, chat & file
                                sharing. Sessions recorded for revision.
                            </p>
                            <ul className="space-y-1.5 text-sm text-slate-600">
                                <li className="flex gap-2"><CheckCircle2 className="w-4 h-4 text-amber-500 mt-0.5 shrink-0" /> All grades · all subjects</li>
                                <li className="flex gap-2"><CheckCircle2 className="w-4 h-4 text-amber-500 mt-0.5 shrink-0" /> Free demo before payment</li>
                                <li className="flex gap-2"><CheckCircle2 className="w-4 h-4 text-amber-500 mt-0.5 shrink-0" /> Pay in {data.currency} — no contract lock-in</li>
                            </ul>
                            <Link to="/online-classes" className="mt-4 inline-flex items-center gap-1 text-[#1E3A8A] font-semibold text-sm hover:gap-2 transition-all">
                                Learn more about online classes <ArrowRight className="w-4 h-4" />
                            </Link>
                        </div>
                        <div className="bg-white border border-slate-200 rounded-2xl p-6 hover:border-blue-200 transition-colors">
                            <div className="w-12 h-12 rounded-xl bg-blue-50 text-[#1E3A8A] flex items-center justify-center mb-4">
                                <Globe2 className="w-6 h-6" />
                            </div>
                            <h3 className="font-heading text-xl font-semibold mb-2">NRI / Indian Curriculum (CBSE & ICSE)</h3>
                            <p className="text-slate-600 leading-relaxed text-sm mb-3">
                                Dedicated Indian-curriculum program for Indian-origin students living in {data.fullName} —
                                so your child stays aligned with school back home.
                            </p>
                            <ul className="space-y-1.5 text-sm text-slate-600">
                                <li className="flex gap-2"><CheckCircle2 className="w-4 h-4 text-amber-500 mt-0.5 shrink-0" /> Class 1 to 12 — CBSE / ICSE / IB / IGCSE</li>
                                <li className="flex gap-2"><CheckCircle2 className="w-4 h-4 text-amber-500 mt-0.5 shrink-0" /> Aligned with NCERT / school exams in India</li>
                                <li className="flex gap-2"><CheckCircle2 className="w-4 h-4 text-amber-500 mt-0.5 shrink-0" /> Time-zone matched to {data.name} after-school</li>
                            </ul>
                            <Link to="/foreign-students" className="mt-4 inline-flex items-center gap-1 text-[#1E3A8A] font-semibold text-sm hover:gap-2 transition-all">
                                NRI / Foreign Students program <ArrowRight className="w-4 h-4" />
                            </Link>
                        </div>
                    </div>
                </div>
            </section>

            {/* V6 interlink: topical entry pages for this country */}
            <section className="py-16 bg-white">
                <div className="max-w-7xl mx-auto px-6 lg:px-8">
                    <div className="text-xs font-semibold uppercase tracking-[0.3em] text-[#3B82F6] mb-3">
                        Browse by topic for {data.fullName}
                    </div>
                    <h2 className="font-heading text-3xl sm:text-4xl font-bold mb-3">
                        Subject, board & exam entry pages for {data.name} families
                    </h2>
                    <p className="text-slate-600 mb-8 max-w-3xl">
                        Jump straight to the dedicated 1:1 program page for your child's subject, board or competitive exam.
                    </p>
                    <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-3">
                        {[
                            [`Online Maths Tuition in ${data.name}`, `/online-maths-tuition-${data.slug}`],
                            [`Online Physics Tuition in ${data.name}`, `/online-physics-tuition-${data.slug}`],
                            [`Online CBSE Tuition in ${data.name}`, `/online-cbse-tuition-${data.slug}`],
                            [`Online ICSE Tuition in ${data.name}`, `/online-icse-tuition-${data.slug}`],
                            [`NEET Coaching for NRI Students in ${data.name}`, `/online-neet-coaching-for-nri-students-${data.slug}`],
                            [`IIT-JEE Coaching for NRI Students in ${data.name}`, `/online-jee-coaching-for-nri-students-${data.slug}`],
                        ].map(([label, to]) => (
                            <Link
                                key={to}
                                to={to}
                                className="bg-[#F8FAFC] border border-slate-200 hover:border-blue-300 hover:bg-white rounded-lg px-4 py-3 text-sm text-slate-700 flex items-center justify-between gap-2 transition-colors group"
                            >
                                <span>{label}</span>
                                <ArrowRight className="w-4 h-4 text-slate-400 group-hover:text-[#1E3A8A] group-hover:translate-x-0.5 transition-transform" />
                            </Link>
                        ))}
                    </div>
                </div>
            </section>
            {/* Testimonials — rotating pool (Batch 2G), or single fallback */}
            {(() => {
                const pool = getTestimonialsForSlug(countrySlug);
                const items = pool.length >= 2 ? pool : (data.testimonial ? [data.testimonial] : []);
                if (items.length === 0) return null;
                return <RotatingTestimonials items={items} variant="dark" />;
            })()}

            {/* FAQ + Registration */}
            <section className="py-16 bg-white">
                <div className="max-w-7xl mx-auto px-6 lg:px-8 grid lg:grid-cols-5 gap-12">
                    <div className="lg:col-span-3">
                        <div className="text-xs font-semibold uppercase tracking-[0.3em] text-[#3B82F6] mb-3">
                            {data.name} FAQs
                        </div>
                        <h2 className="font-heading text-3xl sm:text-4xl font-bold mb-8">
                            Common questions from {data.name} parents
                        </h2>
                        <div className="space-y-3">
                            {data.faqs.map(([q, a]) => (
                                <details
                                    key={q}
                                    className="group bg-[#F8FAFC] border border-slate-200 rounded-xl p-5 [&_summary::-webkit-details-marker]:hidden"
                                >
                                    <summary className="cursor-pointer font-heading font-semibold text-[#0F172A] flex items-center justify-between">
                                        {q}
                                        <span className="text-[#3B82F6] text-2xl group-open:rotate-45 transition-transform">+</span>
                                    </summary>
                                    <p className="mt-3 text-sm text-slate-600 leading-relaxed">{a}</p>
                                </details>
                            ))}
                        </div>
                        <div className="mt-8 flex flex-wrap gap-3 text-sm">
                            <Link to="/online-classes" className="inline-flex items-center gap-1 text-[#1E3A8A] font-semibold hover:gap-2 transition-all">
                                1:1 Online Classes <ArrowRight className="w-4 h-4" />
                            </Link>
                            <span className="text-slate-300">·</span>
                            <Link to="/foreign-students" className="inline-flex items-center gap-1 text-[#1E3A8A] font-semibold hover:gap-2 transition-all">
                                NRI / Foreign Students <ArrowRight className="w-4 h-4" />
                            </Link>
                            <span className="text-slate-300">·</span>
                            <Link to="/courses" className="inline-flex items-center gap-1 text-[#1E3A8A] font-semibold hover:gap-2 transition-all">
                                All courses <ArrowRight className="w-4 h-4" />
                            </Link>
                            <span className="text-slate-300">·</span>
                            <Link to="/blog" className="inline-flex items-center gap-1 text-[#1E3A8A] font-semibold hover:gap-2 transition-all">
                                Tutoring blog <ArrowRight className="w-4 h-4" />
                            </Link>
                        </div>
                    </div>
                    <div className="lg:col-span-2" id="book-demo">
                        <RegistrationForm
                            title={`Book Free Demo · ${data.name}`}
                            defaultCity={data.fullName}
                        />
                    </div>
                </div>
            </section>

            {/* Other countries (internal linking) */}
            <section className="py-12 bg-[#F8FAFC] border-t border-slate-200">
                <div className="max-w-6xl mx-auto px-6 lg:px-8 text-center">
                    <div className="text-xs font-semibold uppercase tracking-[0.3em] text-[#3B82F6] mb-2">
                        Also Available In
                    </div>
                    <h3 className="font-heading text-xl font-semibold mb-4">
                        Anand Tutorials online tuition in other countries
                    </h3>
                    <div className="flex flex-wrap justify-center gap-2">
                        {Object.values(COUNTRIES_DATA)
                            .filter((c) => c.slug !== data.slug)
                            .map((c) => (
                                <Link
                                    key={c.slug}
                                    to={`/${c.urlSlug}`}
                                    className="px-4 py-2 bg-white border border-slate-200 rounded-lg text-sm font-semibold text-[#1E3A8A] hover:border-blue-200 hover:shadow-sm"
                                >
                                    {c.flag} Online tuition in {c.name} →
                                </Link>
                            ))}
                        <Link
                            to="/foreign-students"
                            className="px-4 py-2 bg-white border border-slate-200 rounded-lg text-sm font-semibold text-[#1E3A8A] hover:border-blue-200 hover:shadow-sm"
                        >
                            Foreign Students (Global) →
                        </Link>
                    </div>
                </div>
            </section>

            {/* V65 — AEO Definition block for "1:1 online tuition in {country}" entity */}
            <DefinitionBox
                term={countryAEO.definition.term}
                definition={countryAEO.definition.definition}
                examples={countryAEO.definition.examples}
                testId={`country-${data.slug}-definition`}
            />

            {/* V65 — AEO Conversational FAQ (voice-search + LLM ingestion) */}
            <ConversationalFAQ
                heading={`Deeper questions from ${data.fullName} parents`}
                subheading={`Long-form answers to the questions ${data.fullName} families most often ask us on enquiry calls.`}
                items={countryAEO.faqs}
                testId={`country-${data.slug}-conversational-faq`}
            />

            {/* Final CTA */}
            <section className="py-16 bg-gradient-to-r from-[#1E3A8A] to-[#1E40AF] text-white">
                <div className="max-w-5xl mx-auto px-6 lg:px-8 text-center">
                    <h2 className="font-heading text-3xl sm:text-4xl font-bold mb-3">
                        Start your child's success story in {data.fullName}.
                    </h2>
                    <p className="text-blue-100 max-w-2xl mx-auto mb-6">
                        Free 1:1 demo with a verified tutor at your preferred {data.timezones.split("·")[0].trim()} slot. No
                        payment, no commitment.
                    </p>
                    <div className="flex flex-wrap justify-center gap-3">
                        <a
                            href={`tel:${ENQUIRY_PHONE_RAW}`}
                            className="inline-flex items-center gap-2 bg-amber-500 text-[#0F172A] font-semibold px-6 py-3 rounded-lg hover:bg-amber-400"
                        >
                            <Phone className="w-4 h-4" /> Call India Team
                        </a>
                        <Link
                            to="/register"
                            className="inline-flex items-center gap-2 bg-white text-[#1E3A8A] font-semibold px-6 py-3 rounded-lg hover:bg-slate-50"
                        >
                            Book Free Demo
                        </Link>
                        <a
                            href={`https://wa.me/${SOCIAL.whatsapp}?text=${encodeURIComponent(
                                `Hi Anand Tutorials, I am from ${data.fullName} and would like to enquire.`
                            )}`}
                            target="_blank"
                            rel="noreferrer"
                            className="inline-flex items-center gap-2 bg-[#25D366] text-white font-semibold px-6 py-3 rounded-lg hover:bg-[#1ebe5a]"
                        >
                            <MessageCircle className="w-4 h-4" /> WhatsApp
                        </a>
                    </div>
                </div>
            </section>
        </div>
    );
}
